import React from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";

import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { TASK_PRIORITY_OPTIONS, buildUpdateTaskPayload, toDateInputValue } from "../../pages/Tasks/taskUtils";

// Formulário de edição — só title/description/priority/dueDate, igual ao
// payload aceito por PUT /tasks/:taskId. Pré-preenchido com os valores
// atuais da tarefa (o backend trata reenvio do mesmo valor como no-op).
const TaskEditForm = ({ task, onSubmit, onCancel }) => {
  const initialValues = {
    title: task.title || "",
    description: task.description || "",
    priority: task.priority || "medium",
    dueDate: toDateInputValue(task.dueDate)
  };

  const EditSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(3, i18n.t("tasks.modal.errors.titleTooShort"))
      .max(255, i18n.t("tasks.modal.errors.titleTooLong"))
      .required(i18n.t("tasks.modal.errors.titleRequired")),
    description: Yup.string().max(5000, i18n.t("tasks.modal.errors.descriptionTooLong")),
    priority: Yup.string().oneOf(TASK_PRIORITY_OPTIONS),
    dueDate: Yup.string().nullable()
  });

  const handleSubmit = async (values, actions) => {
    const payload = buildUpdateTaskPayload(values);
    const success = await onSubmit(payload);
    actions.setSubmitting(false);

    if (success) {
      toast.success(i18n.t("tasks.details.editSuccess"));
    }
    // Em caso de erro, o formulário permanece aberto com os dados digitados
    // preservados (Formik não reseta os values automaticamente).
  };

  return (
    <Formik initialValues={initialValues} validationSchema={EditSchema} onSubmit={handleSubmit}>
      {({ values, errors, touched, setFieldValue, isSubmitting }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                as={TextField}
                name="title"
                label={i18n.t("tasks.modal.title")}
                variant="outlined"
                size="small"
                fullWidth
                required
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
            </Grid>

            <Grid item xs={12}>
              <Field
                as={TextField}
                name="description"
                label={i18n.t("tasks.modal.description")}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={3}
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="task-edit-priority-label">{i18n.t("tasks.modal.priority")}</InputLabel>
                <Select
                  labelId="task-edit-priority-label"
                  label={i18n.t("tasks.modal.priority")}
                  value={values.priority}
                  onChange={e => setFieldValue("priority", e.target.value)}
                >
                  {TASK_PRIORITY_OPTIONS.map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {i18n.t(`tasks.priority.${priority}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name="dueDate"
                type="date"
                label={i18n.t("tasks.modal.dueDate")}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }} mt={2}>
            <Button onClick={onCancel} disabled={isSubmitting} color="secondary" variant="outlined">
              {i18n.t("tasks.details.actions.cancelButton")}
            </Button>
            <ButtonWithSpinner type="submit" color="primary" variant="contained" loading={isSubmitting}>
              {i18n.t("tasks.details.actions.saveEdit")}
            </ButtonWithSpinner>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default TaskEditForm;
